import {
  AllowedDeliveryChannel,
  PermissionCategoryDto,
  PermissionTypeDto,
  SubscriptionAddonType,
  SubscriptionAppliance,
  SubscriptionPlanType
} from "../enums";
import { CampaignSettings } from "./widgets";

export interface AuthContextConfigurationGetDto {
  /** @deprecated Use Features property instead. */
  hasCfs?: boolean;
  features?: PermissionTypeDto[];
  featureSettings?: FeatureSettingsDto;
  limits: SubscriptionLimitsDto;
}

export interface AccountSubscriptionDetailsDto {
  plan: SubscriptionPlanGetDto;
  addons: SubscriptionAddonGetDto[];
  externalFeatures: ExternalFeatureGetDto[];
  features: PermissionTypeDto[];
  widgets: DefaultWidgetGetDto[];
  featureSettings: FeatureSettingsDto;
}

export interface FeatureSettingsDto {
  terminal: TerminalFeatureSettingsDto;
  distribution: DistributionFeatureSettingsDto;
  paidLocationCount: number;
  addedLocationCount: number;
  navigateHome?: NavigateHomeFeatureSettingsDto;
}

export interface TerminalFeatureSettingsDto {
  limit: number;
}

export interface DistributionFeatureSettingsDto {
  smsCreditsLimit: number;
}

export interface NavigateHomeFeatureSettingsDto {
  label: string;
}

export interface DefaultWidgetGetDto {
  id: number;
  name: string;
  description: string;
  notificationKey: string;
  packageId: number;
  isWidget: boolean;
  isCampaign: boolean;
  isAvailableForConditions: boolean;
  isAvailableForKiosk: boolean;
  useForUnified: boolean;
  useForClickableTemplate: boolean;
  useOnCfs: boolean;
  deliveryMethod: AllowedDeliveryChannel;
  videoId: string;
  campaignSettings?: CampaignSettings;
}

export interface ExternalFeatureGetDto {
  id: number;
  name: string;
  code: string;
  url: string;
  platformId: number;
  categoryId: number;
}

export interface SubscriptionAddonGetDto {
  id: number; // Also uses as key for translations
  name: string;
  description: string;
  defaultPriceConfiguration: PriceConfiguration;
  addonType: SubscriptionAddonType;
  info: string;
  imageUrl: string;
  limits: SubscriptionAddonLimits;
  features: PermissionTypeDto[];
  applianceType: SubscriptionAppliance;
  isCountable: boolean;
  quantity: number;
}

export interface SubscriptionAddonLimits {
  dailyLimit: number;
  periodLimit: number;
}

export interface SubscriptionLimitsDto {
  couponLimit: number;
  reviewRequestLimit: number;
  customShortLinkLimit: number;
  bulkSize: number;
  socialLinkLimit: number;
  customAppLimit: number;
}

export interface PriceConfiguration {
  price: number;
  productId: string;
  priceTable: Record<number, number>;
}

export interface SubscriptionPlanGetDto {
  id: number;
  name: string;
  description: string;
  limits: SubscriptionPlanLimits;
  options: SubscriptionPlanOptionsDto;
  price: number;
  periodInDays: number;
  productId: string;
  features: any;
  planType: SubscriptionPlanType;
  planFamily?: string;
  widgets: DefaultWidgetGetDto[];
  externalFeatures: ExternalFeatureGetDto[];
}

export interface PartnerAssignedEligibleAddonDto {
  subscriptionAddonId: number;
}

export interface PartnerEligibleSubscriptionPlanGetDto {
  plan: SubscriptionPlanGetDto;
  partnerAssignedEligibleAddons: PartnerAssignedEligibleAddonDto[];
}

export interface SubscriptionPlan extends SubscriptionPlanGetDto {
  locationQuantity: number;
  addons: SubscriptionAddons[];
}

export interface SubscriptionPlanLimits {
  dailyLimit: number;
  dailyWarningCap: number;
  trialDailyLimit: number;
  trialDailyWarningCap: number;
  periodLimit: number;
  periodOverCapacity: number;
  periodWarningCap: number;
  trialPeriodLimit: number;
  trialPeriodOverCapacity: number;
  trialPeriodWarningCap: number;
  couponLimit?: number;
  reviewRequestLimit?: number;
  customShortLinkLimit: number;
  bulkSize: number;
  socialLinkLimit: number;
  isUnlimited: boolean;
}

export interface SubscriptionPlanOptionsDto {
  features: PermissionTypeDto[];
  showOnPlanChooser: boolean;
  canChangeLocationsQuantity: boolean;
  hasDashboard: boolean;
  hasCmc: boolean;
  hasCfs: boolean;
  hasTrackingPixel: boolean;
  hasOutboundMarketing: boolean;
  hasReportingAndAnalytics: boolean;
  hasProducts: boolean;
  hasExternalContacts: boolean;
  hasPosIntegration: boolean;
  hasConnectors: boolean;
  hasCfsPrint: boolean;
  hasDistribution: boolean;
  hasCustomers: boolean;
  canViewBulks: boolean;
  canCreateBulks: boolean;
  canDeleteBulks: boolean;
  hasAdvancedTools: boolean;
  hasUrlsAndQrCodes: boolean;
  hasAppsShortUrlsAndQrs: boolean;
  hasCustomShortUrlsAndQrs: boolean;
  hasBulkLinksAndQrsList: boolean;
  hasLoyaltyProgram: boolean;
  hasLoyaltyProgramReporting: boolean;
  hasThirdPartyAnalytics: boolean;
  hasDevices: boolean;
  hasPrintableQrCodeFlyers: boolean;
  hasPrintReceipt: boolean;
  hasMarketingCampaigns: boolean;
  enabledForPartnerPayment: boolean;
  enabledForNewRegistration: boolean;
  partnerEligibility?: boolean;
}

export interface AccountSubscriptionPostDto {
  subscriptionPlanId: number;
  locationQuantity: number;
  addons?: AccountSubscriptionAddonPostDto[];
}

export interface AccountSubscriptionAddonPostDto {
  quantity: number;
  subscriptionAddonId: number;
  applianceType: SubscriptionAppliance;
}

export interface SubscriptionGetDto {
  subscriptionPlanId: number;
  isTrial: boolean;
  startDate: Date; // string ISO?
  endDate: Date; // string ISO?
  dailyLimit: number;
  dailyWarningCap: number;
  periodLimit: number;
  periodOverCapacity: number;
  periodWarningCap: number;
  dailyCount: number;
  count: number;
}

export interface AccountSubscriptionGetDto {
  accountId: number;
  subscriptionPlanId: number;
  locationQuantity: number;
  addons: AccountSubscriptionAddonGetDto[];
}

export interface AccountSubscriptionAddonGetDto {
  subscriptionAddonId: number;
  quantity: number;
  applianceType: SubscriptionAppliance;
  limits: SubscriptionAddonLimits;
}

export interface SubscriptionAddonDetailsDto {
  addon: SubscriptionAddonGetDto;
  externalFeatures: ExternalFeatureGetDto[];
  widgets: DefaultWidgetGetDto[];
}

export interface SubscriptionAddons {
  id: number;
  name: string;
  description: string;
  defaultPriceConfiguration: PriceConfiguration;
  addonType: SubscriptionAddonType;
  quantity?: number;
  imageUrl: string;
  info: string;
  isActive: boolean;
}

export interface SubscriptionPlanFilterPostDto {
  sourceType?: SubscriptionPlanType;
  showOnPlanChooser?: boolean;
  enabledForPartnerPayment?: boolean;
  enabledForNewRegistration?: boolean;
  eligibleForPartner?: boolean;
  partnerId?: number;
  planFamily?: string;
}

export interface SubscriptionPlanDetailsDto {
  plan: SubscriptionPlanGetDto;
  addons: SubscriptionAddonGetDto[];
  externalFeatures: string[]; // todo type ExternalFeatureGetDto?
  widgets: Campaign[];
  featureInfos: PlanFeaturesAdditionalInfoDto;
}
export interface PlanFeaturesAdditionalInfoDto {
  price: number;
  locationCount: number;
  widgetCount: number;
  userCountLimit: number;
  customerInteractionMonthLimit: number;
  freeTrialPeriodInDays: number;
  hardwareCount: number;
  contractPeriod: number;
  customShortLinkLimit: number;
}

export interface SubscriptionPlansWithAddonsGetDto {
  plans: SubscriptionPlanDetailsDto[];
  addons: SubscriptionAddonDetailsDto[];
}

/** @deprecated */
export interface FeatureMappingGetDto {
  feature: PermissionTypeDto;
  minPlan: SubscriptionPlanShortGetDto;
}

export interface PermissionMappingGetDto {
  permission: PermissionTypeDto;
  minPlan: SubscriptionPlanShortGetDto;
}

export interface SubscriptionPlanShortGetDto {
  id: number;
  name: string;
  description: string;
  price: number;
  productId: number;
}

type Campaign = any; // fixme type

export interface PermissionCategoryCheckResponseDto {
  category: PermissionCategoryDto;
  permissions: PermissionTypeDto[];
}
